import React, { useState } from "react";
import { NavLink, useLocation, Outlet } from "react-router-dom";
import {
  Compass, ShieldCheck, Rocket, Bot, Users, Grid3X3, BarChart3, Radar,
  MessageCircleQuestion, KanbanSquare, CalendarClock, Megaphone, Gauge, FileText,
  GitBranch, Play, RotateCcw, PanelRightClose, PanelRightOpen, Star, RefreshCcw, LayoutGrid,
  Database, Radio, GitMerge, Microscope, FileSignature, Bell
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ROLES, getRole } from "@/lib/roles";
import { StatusChip, PersonaStamp } from "@/components/app/primitives";
import { SimulationOverlay } from "@/components/app/SimulationOverlay";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WorkspaceSwitcher } from "@/components/app/WorkspaceSwitcher";
import { UserMenu } from "@/components/app/UserMenu";

const ALERT_TONE = { info: "ai", warning: "risk", critical: "danger" };

const AlertsBell = () => {
  const { roleKey } = useApp();
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = React.useCallback(() => {
    api.get("/live/alerts?limit=25").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const unack = alerts.filter((a) => !a.acknowledged).length;

  const ack = async (id) => {
    try {
      await api.patch(`/live/alerts/${id}`, { actor_role: roleKey });
      load();
    } catch (e) { toast.error("Acknowledge failed"); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="alerts-bell-button" title="Live data alerts"
          className="relative inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] bg-[--shell-3] hover:text-[--tx-1] transition-colors">
          <Bell size={13} />
          {unack > 0 && (
            <span data-testid="alerts-bell-badge"
              className="absolute -top-1 -right-1 min-w-[15px] h-[15px] rounded-full bg-[--six-crimson] text-white text-[9px] font-bold flex items-center justify-center px-0.5">
              {unack}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 bg-[--shell-1] border-[--hairline]" data-testid="alerts-popover">
        <div className="px-3.5 py-2.5 border-b border-[--divider] flex items-center justify-between">
          <span className="text-[11px] font-mono uppercase tracking-widest text-[--tx-2]">Live data alerts</span>
          <span className="text-[10px] font-mono text-[--tx-3]">{unack} unacknowledged</span>
        </div>
        <div className="max-h-[380px] overflow-y-auto thin-scroll">
          {alerts.length === 0 && (
            <div className="px-3.5 py-6 text-center text-[11px] text-[--tx-3]">
              No alerts. New uploads and calculation passes generate alerts when drivers shift or evidence flips.
            </div>
          )}
          {alerts.map((a) => (
            <div key={a.id} className={`px-3.5 py-2.5 border-b border-[--divider] ${a.acknowledged ? "opacity-50" : ""}`} data-testid={`alert-row-${a.id}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <StatusChip tone={ALERT_TONE[a.severity] || "neutral"} className="!text-[9px] capitalize">{a.severity}</StatusChip>
                <span className="text-[9px] font-mono text-[--tx-3]">{(a.ts || "").slice(5, 16).replace("T", " ")}</span>
                {!a.acknowledged && (
                  <button onClick={() => ack(a.id)} data-testid={`alert-ack-${a.id}`}
                    className="ml-auto text-[10px] font-semibold text-[--ai] hover:text-[#35B7B1] transition-colors">
                    Acknowledge
                  </button>
                )}
              </div>
              <div className="text-[11px] font-semibold text-[--tx-1] leading-snug">{a.title}</div>
              <div className="text-[10px] text-[--tx-3] leading-snug mt-0.5">{a.detail?.slice(0, 140)}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/overview", label: "Transformation OS", icon: LayoutGrid },
    ],
  },
  {
    label: "Orientation",
    items: [
      { to: "/briefing", label: "Consortium Briefing", icon: Compass },
      { to: "/flow", label: "Judgment to Execution", icon: GitBranch },
    ],
  },
  {
    label: "Intake & Analysis",
    items: [
      { to: "/evidence", label: "Evidence Room", icon: ShieldCheck },
      { to: "/launch", label: "Launch Console", icon: Rocket },
      { to: "/agents", label: "Agentic Analysis Studio", icon: Bot },
      { to: "/triad", label: "Triad Review Room", icon: Users },
    ],
  },
  {
    label: "Assessment",
    items: [
      { to: "/matrix", label: "Assessment Matrix", icon: Grid3X3 },
      { to: "/analytics", label: "Analytics Workspace", icon: BarChart3 },
      { to: "/control-tower", label: "Gap / Risk Control Tower", icon: Radar },
    ],
  },
  {
    label: "Execution",
    items: [
      { to: "/questions", label: "Question Builder", icon: MessageCircleQuestion },
      { to: "/pilots", label: "Pilot Board", icon: KanbanSquare },
      { to: "/brightspeed", label: "Change Management", icon: RefreshCcw },
      { to: "/impact-engine", label: "30-Day Impact Engine", icon: CalendarClock },
    ],
  },
  {
    label: "Executive",
    items: [
      { to: "/command-center", label: "Command Center", icon: Gauge },
      { to: "/comms", label: "Communications Studio", icon: Megaphone },
      { to: "/reports", label: "Report Builder", icon: FileText },
    ],
  },
  {
    label: "Live Data & Intelligence",
    live: true,
    items: [
      { to: "/live/intake", label: "Data Intake Hub", icon: Database },
      { to: "/live/mapping", label: "Data Mapping Studio", icon: GitMerge },
      { to: "/live/forensic", label: "August Forensic", icon: Microscope },
      { to: "/live/runs", label: "Agentic Analysis Runs", icon: Bot },
      { to: "/live/sop", label: "SOP Builder", icon: FileSignature },
    ],
  },
];

const RoleLensBar = () => {
  const { roleKey } = useApp();
  const location = useLocation();
  const role = getRole(roleKey);
  const focus = role.focus[location.pathname];
  if (!focus) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[--divider] bg-[--shell-2] px-3 py-2 mb-4" data-testid="role-lens-bar">
      <StatusChip tone="ai" className="mt-0.5 shrink-0">{role.short} lens</StatusChip>
      <p className="text-xs text-[--tx-2] leading-relaxed">{focus}</p>
    </div>
  );
};

const RightInsightsPanel = ({ open }) => {
  const { overview, roleKey } = useApp();
  const location = useLocation();
  const role = getRole(roleKey);
  if (!open) return null;
  const counts = overview?.counts;
  const state = overview?.state;
  return (
    <aside className="hidden xl:flex flex-col w-[300px] shrink-0 border-l border-[--divider] bg-[--shell-1] overflow-y-auto thin-scroll" data-testid="right-insights-panel">
      <div className="p-4 border-b border-[--divider]">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-2">Consortium lenses</div>
        <div className="space-y-2.5">
          {[
            { code: "TP", name: "Transformation Partner", note: "Business value, governance, 30/90/180" },
            { code: "DS", name: "AI/Data Strategist", note: "Evidence, confidence, measurement" },
            { code: "TO", name: "The Operator", note: "Operating reality, frontline, customer" },
          ].map((p) => (
            <div key={p.code} className="flex items-center gap-2.5">
              <PersonaStamp code={p.code} size="sm" />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[--tx-1] truncate">{p.name}</div>
                <div className="text-[10px] text-[--tx-3] truncate">{p.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="p-4 border-b border-[--divider]">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-2">Engagement pulse</div>
        {counts ? (
          <div className="grid grid-cols-2 gap-2">
            {[
              { l: "Evidence in", v: `${counts.evidence_received + counts.evidence_partial}/${counts.evidence_total}` },
              { l: "Gaps", v: counts.gaps },
              { l: "Risks", v: counts.risks },
              { l: "Live issues", v: counts.issues },
              { l: "Pilots ready", v: counts.pilots_ready },
              { l: "Decisions open", v: counts.decisions_open },
            ].map((s) => (
              <div key={s.l} className="rounded-lg bg-[--panel-dark] border border-[--divider] px-2.5 py-2">
                <div className="font-display text-lg font-semibold text-[--tx-1] tabular-nums">{s.v}</div>
                <div className="text-[10px] text-[--tx-3]">{s.l}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-[--tx-3]">Loading...</div>
        )}
      </div>
      <div className="p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-2">Role focus - {role.short}</div>
        <p className="text-xs text-[--tx-2] leading-relaxed">{role.focus[location.pathname] || role.description}</p>
        {state && (
          <div className="mt-4 rounded-lg border border-[--divider] bg-[--panel-dark] p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Working ambition</div>
            <p className="text-xs text-[--tx-2] leading-relaxed">{state.config?.pilot_ambition}</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export const AppShell = () => {
  const { overview, roleKey, setRoleKey, simActive, startSimulation, stopSimulation, refreshOverview, mode, setMode } = useApp();
  const [rightOpen, setRightOpen] = useState(true);
  const role = getRole(roleKey);
  const state = overview?.state;
  const location = useLocation();

  // Entering a live workspace always switches the session to Live Engagement mode
  React.useEffect(() => {
    if (location.pathname.startsWith("/live/") && mode !== "live") setMode("live");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleReset = async () => {
    try {
      await api.post("/demo/reset", { confirm: "RESET_DEMO" });
      toast.success("Demo reset to baseline state");
      refreshOverview();
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[--shell-0] shell-noise overflow-hidden">
      {/* Top bar */}
      <header className="h-14 shrink-0 flex items-center gap-3 px-4 border-b border-[--divider] bg-[--shell-1] relative z-10" data-testid="top-status-bar">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="bg-[#F5F7FA] rounded-lg px-1.5 py-1 shrink-0">
            <img src="/six-logo.jpg" alt="SIX" className="h-6 w-auto" data-testid="six-logo" />
          </div>
          <div className="min-w-0 hidden md:block">
            <div className="font-display text-[13px] font-semibold text-[--tx-1] leading-none truncate" data-testid="app-brand-name">SIX</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mt-0.5" data-testid="app-brand-subtitle">Transformation Center</div>
          </div>
        </div>
        <div className="h-6 w-px bg-[--divider] hidden md:block" />
        <WorkspaceSwitcher />
        <div className="hidden lg:flex items-center gap-2 min-w-0">
          <StatusChip tone="crimson" data-testid="topbar-phase-chip">{state?.phase || "Loading"}</StatusChip>
          <StatusChip tone="ai" data-testid="topbar-confidence-chip">{state?.overall_confidence || "..."}</StatusChip>
          <span className="text-xs text-[--tx-3] font-mono hidden 2xl:inline" data-testid="topbar-objective">OCR 18% → 15% · Q3 15.5%</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <AlertsBell />
          <div className="hidden md:flex items-center rounded-lg border border-[--hairline] bg-[--shell-3] p-0.5" data-testid="mode-toggle">
            <button onClick={() => setMode("demo")} data-testid="mode-demo-button"
              className={`px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors ${mode === "demo" ? "bg-[--shell-1] text-[--tx-1] border border-[--hairline]" : "text-[--tx-3] hover:text-[--tx-2]"}`}>
              Demo
            </button>
            <button onClick={() => setMode("live")} data-testid="mode-live-button"
              className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors ${mode === "live" ? "bg-[--ai-soft] text-[--ai] border border-white/10" : "text-[--tx-3] hover:text-[--tx-2]"}`}>
              <Radio size={10} className={mode === "live" ? "lane-live" : ""} /> Live
            </button>
          </div>
          <Select value={roleKey} onValueChange={setRoleKey}>
            <SelectTrigger className="w-[190px] h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid="role-toggle">
              <SelectValue placeholder="Role view" />
            </SelectTrigger>
            <SelectContent className="bg-[--shell-2] border-[--hairline]">
              {ROLES.map((r) => (
                <SelectItem key={r.key} value={r.key} className="text-xs" data-testid={`role-option-${r.key}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={simActive ? stopSimulation : startSimulation}
            data-testid="simulation-mode-toggle"
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-8 text-xs font-semibold transition-colors active:scale-[0.98] ${simActive ? "bg-[--six-crimson] text-white hover:bg-[--six-crimson-hover]" : "bg-[--shell-3] text-[--tx-2] border border-[--hairline] hover:text-[--tx-1] hover:bg-[#243041]"}`}
          >
            <Play size={13} /> {simActive ? "Simulating" : "Simulation"}
          </button>
          <button onClick={handleReset} data-testid="demo-reset-button" title="Reset demo state"
            className="inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] bg-[--shell-3] hover:text-[--tx-1] hover:bg-[#243041] transition-colors">
            <RotateCcw size={13} />
          </button>
          <button onClick={() => setRightOpen(!rightOpen)} data-testid="right-panel-toggle" title="Toggle insights panel"
            className="hidden xl:inline-flex items-center justify-center rounded-lg w-8 h-8 text-[--tx-3] border border-[--hairline] bg-[--shell-3] hover:text-[--tx-1] hover:bg-[#243041] transition-colors">
            {rightOpen ? <PanelRightClose size={13} /> : <PanelRightOpen size={13} />}
          </button>
          <UserMenu />
        </div>
      </header>

      <div className="flex flex-1 min-h-0 relative z-10">
        {/* Left nav */}
        <nav className="w-[236px] shrink-0 border-r border-[--divider] bg-[--shell-1] overflow-y-auto thin-scroll py-3 hidden md:block" data-testid="left-module-nav">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-3">
              <div className={`px-4 pb-1.5 text-[10px] font-mono uppercase tracking-[0.14em] ${group.live ? "text-[--ai]" : "text-[--tx-3]"} flex items-center gap-1.5`}>
                {group.live && <span className="w-1.5 h-1.5 rounded-full bg-[--ai] lane-live" />}
                {group.label}
              </div>
              {group.items.map((item) => {
                const Icon = item.icon;
                const emphasized = role.emphasized.includes(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`left-nav-module-link-${item.to.slice(1)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-4 py-[7px] text-[13px] font-medium transition-colors ${isActive ? "nav-item-active text-[--tx-1]" : "text-[--tx-2] hover:text-[--tx-1] hover:bg-white/[0.03]"}`
                    }
                  >
                    <Icon size={15} className="shrink-0 opacity-70" />
                    <span className="truncate">{item.label}</span>
                    {emphasized && <Star size={9} className="ml-auto shrink-0 text-[--amber] fill-[--amber]" title={`Emphasized for ${role.short}`} />}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Center workspace */}
        <main className="flex-1 min-w-0 overflow-y-auto thin-scroll" data-testid="center-workspace">
          <div className="p-4 md:p-6 max-w-[1400px]">
            <RoleLensBar />
            <Outlet />
          </div>
        </main>

        <RightInsightsPanel open={rightOpen} />
      </div>

      <SimulationOverlay />
    </div>
  );
};
